<script setup>
  import { computed, onMounted, ref } from "vue";
  import { useStore } from "vuex";

  /* =========================================
   LUCIDE ICONS
========================================= */
  import {
    LayoutDashboard,
    Settings,
    Palette,
    Images,
    School,
    Users,
    GraduationCap,
    Building2,
    ImageIcon,
    CalendarDays,
    Newspaper,
    MessageSquareQuote,
    Search,
    Share2,
    Phone,
    Globe,
    Menu,
    X,
    Sparkles,
  } from "lucide-vue-next";

  /* =========================================
   PROPS & EMITS
========================================= */
  // eslint-disable-next-line no-unused-vars
  const props = defineProps({
    activePage: {
      type: String,
      default: "website-settings",
    },
  });

  const emit = defineEmits(["change-page"]);

  /* =========================================
   STORE
========================================= */
  const store = useStore();

  /* =========================================
   SIDEBAR STATE
========================================= */
  const isMobileSidebarOpen = ref(false);

  /* =========================================
   LIFECYCLE
========================================= */
  onMounted(async () => {
    try {
      await store.dispatch("fetchWebsiteDetails");
    } catch (err) {
      console.error("Failed to fetch website details:", err);
    }
  });

  /* =========================================
   STORE DATA
========================================= */
  const websiteDetails = computed(() => store.getters["getWebsiteDetails"]);

  /* =========================================
   SIDEBAR CONTROLS
========================================= */
  const openMenu = () => {
    isMobileSidebarOpen.value = true;
  };

  const closeMenu = () => {
    isMobileSidebarOpen.value = false;
  };

  /* =========================================
   CHANGE PAGE
========================================= */
  const openPage = (page) => {
    // Parent handles:
    // 1. activePage
    // 2. localStorage
    emit("change-page", page);

    // Close mobile sidebar
    if (window.innerWidth < 1024) {
      closeMenu();
    }
  };

  /* =========================================
   NAVIGATION GROUPS
========================================= */
  const navGroups = [
    {
      label: "Overview",
      items: [
        {
          to: "dashboard",
          label: "Dashboard",
          icon: LayoutDashboard,
        },
        {
          to: "website-settings",
          label: "Website Settings",
          icon: Settings,
        },
        {
          to: "theme-settings",
          label: "Theme Settings",
          icon: Palette,
        },
      ],
    },

    {
      label: "Content",
      items: [
        {
          to: "hero-section",
          label: "Hero Section",
          icon: Images,
        },
        {
          to: "about-school",
          label: "About School",
          icon: School,
        },
        {
          to: "leadership",
          label: "Leadership",
          icon: Users,
        },
        {
          to: "academics",
          label: "Academics",
          icon: GraduationCap,
        },
        {
          to: "facilities",
          label: "Facilities",
          icon: Building2,
        },
      ],
    },

    {
      label: "Media & Engagement",
      items: [
        {
          to: "gallery",
          label: "Gallery",
          icon: ImageIcon,
        },
        {
          to: "events",
          label: "Events",
          icon: CalendarDays,
        },
        {
          to: "notifications",
          label: "Notifications",
          icon: Newspaper,
        },
        {
          to: "campus-infrastructure",
          label: "Campus Infrastructure",
          icon: MessageSquareQuote,
        },
      ],
    },

    {
      label: "Configuration",
      items: [
        {
          to: "seo-settings",
          label: "SEO Settings",
          icon: Search,
        },
        {
          to: "social-links",
          label: "Social Links",
          icon: Share2,
        },
        {
          to: "contact-details",
          label: "Contact Details",
          icon: Phone,
        },
        {
          to: "domain-settings",
          label: "Domain Settings",
          badge: "New",
          icon: Globe,
        },
      ],
    },
  ];
</script>

<template>
  <!-- =========================================
       MOBILE TRIGGER BUTTON
  ========================================== -->
  <button
    v-if="!isMobileSidebarOpen"
    class="lg:hidden fixed top-[85px] left-4 z-[1001] flex items-center gap-3 bg-white/90 backdrop-blur-md border border-slate-200/80 shadow-xl hover:shadow-2xl rounded-2xl px-4 py-3 transition-all duration-300 active:scale-95 group"
    @click="openMenu"
  >
    <div
      class="w-10 h-10 rounded-xl bg-gradient-to-tr from-emerald-500 via-teal-500 to-cyan-500 flex items-center justify-center text-white shadow-md shadow-teal-500/20 group-hover:rotate-6 transition-transform"
    >
      <Menu :size="20" />
    </div>

    <div class="text-left">
      <p class="text-xs font-bold text-slate-800 tracking-wide uppercase">
        Website
      </p>

      <p class="text-xs text-slate-500">Options Panel</p>
    </div>
  </button>

  <!-- =========================================
       OVERLAY
  ========================================== -->
  <transition name="fade">
    <div
      v-if="isMobileSidebarOpen"
      class="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 lg:hidden"
      @click="closeMenu"
    />
  </transition>

  <!-- =========================================
       SIDEBAR CONTAINER
  ========================================== -->
  <aside
    :class="[
      'fixed lg:static top-28 left-0 bottom-12 z-[1000] w-72 h-[calc(100vh-8rem)] bg-white/95 backdrop-blur-xl border-r border-slate-200/80 flex flex-col shadow-2xl lg:shadow-none transition-all duration-300 ease-out rounded-r-3xl overflow-hidden',

      isMobileSidebarOpen
        ? 'translate-x-0 opacity-100'
        : '-translate-x-full opacity-0 lg:translate-x-0 lg:opacity-100',
    ]"
  >
    <!-- =========================================
         BRANDING HEADER
    ========================================== -->
    <div
      class="p-4 border-b border-slate-100 bg-gradient-to-b from-slate-50/50 to-transparent"
    >
      <div class="flex items-center justify-between gap-3">
        <div class="flex items-center gap-3 min-w-0">
          <!-- LOGO -->
          <div class="relative flex-shrink-0">
            <div
              class="w-11 h-11 rounded-2xl bg-gradient-to-tr from-emerald-500 via-teal-500 to-cyan-500 flex items-center justify-center text-white font-black text-base shadow-lg shadow-teal-500/25 ring-2 ring-white overflow-hidden"
            >
              <img
                v-if="websiteDetails?.logo"
                :src="websiteDetails.logo"
                class="w-full h-full object-cover"
                alt="Logo"
              />

              <span v-else>
                {{
                  websiteDetails?.schoolName
                    ?.split(" ")
                    ?.map((w) => w[0])
                    ?.join("")
                    ?.substring(0, 2)
                    ?.toUpperCase() || "ED"
                }}
              </span>
            </div>

            <div
              class="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-500 ring-2 ring-white rounded-full flex items-center justify-center"
            >
              <Sparkles
                :size="8"
                class="text-white"
              />
            </div>
          </div>

          <!-- SCHOOL DETAILS -->
          <div class="min-w-0 flex-1">
            <h3 class="text-sm font-bold text-slate-800 truncate leading-snug">
              {{ websiteDetails?.schoolName || "Loading..." }}
            </h3>

            <span
              class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-600 truncate max-w-full"
            >
              {{
                websiteDetails?.slug
                  ? `${websiteDetails.slug}.educanium.com`
                  : "educanium.com"
              }}
            </span>
          </div>
        </div>

        <!-- MOBILE CLOSE -->
        <button
          class="lg:hidden p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
          @click="closeMenu"
        >
          <X :size="18" />
        </button>
      </div>
    </div>

    <!-- =========================================
         NAVIGATION MENU
    ========================================== -->
    <nav class="flex-1 overflow-y-auto px-3 py-4 space-y-6 custom-scrollbar">
      <div
        v-for="group in navGroups"
        :key="group.label"
        class="space-y-1.5"
      >
        <!-- CATEGORY TITLE -->
        <p
          class="px-3 text-[10px] font-extrabold uppercase tracking-widest text-slate-400/90"
        >
          {{ group.label }}
        </p>

        <!-- NAV ITEMS -->
        <button
          v-for="item in group.items"
          :key="item.to"
          :class="[
            'group relative w-full flex items-center gap-3 px-3.5 py-2.5 rounded-2xl text-sm font-medium transition-all duration-200 overflow-hidden',

            activePage === item.to
              ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/25 font-semibold'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/80 active:scale-[0.98]',
          ]"
          @click="openPage(item.to)"
        >
          <!-- ICON -->
          <component
            :is="item.icon"
            :size="19"
            :stroke-width="activePage === item.to ? 2.3 : 1.8"
            :class="[
              'flex-shrink-0 transition-transform duration-200 group-hover:scale-110',

              activePage === item.to
                ? 'text-white'
                : 'text-slate-400 group-hover:text-blue-600',
            ]"
          />

          <!-- LABEL -->
          <span class="truncate flex-1 text-left tracking-tight">
            {{ item.label }}
          </span>

          <!-- BADGE -->
          <span
            v-if="item.badge"
            :class="[
              'px-2 py-0.5 text-[10px] font-bold rounded-full uppercase tracking-wider transition-colors',

              activePage === item.to
                ? 'bg-white/20 text-white backdrop-blur-md'
                : 'bg-emerald-100 text-emerald-700 group-hover:bg-emerald-200',
            ]"
          >
            {{ item.badge }}
          </span>

          <!-- ACTIVE SHINE -->
          <div
            v-if="activePage === item.to"
            class="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out pointer-events-none"
          />
        </button>
      </div>
    </nav>
  </aside>
</template>

<style scoped>
  /* =========================================
   FADE TRANSITION
========================================= */
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.25s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  /* =========================================
   CUSTOM SCROLLBAR
========================================= */
  .custom-scrollbar::-webkit-scrollbar {
    width: 4px;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(203, 213, 225, 0.5);
    border-radius: 9999px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(148, 163, 184, 0.8);
  }
</style>
